import { LinkAnalysisPage } from '@signals/link-analysis-ui';
import { PatternOfLifePage } from '@signals/pol-ui';
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { useShallow } from 'zustand/react/shallow';
import { useAuthStore } from './stores';
import Layout from './components/Layout/Layout';
import LoadingSpinner from './components/common/LoadingSpinner';
import ProtectedRoute from './components/auth/ProtectedRoute';
import FeatureGate from './components/auth/FeatureGate';
import SessionExpirationWarning from './components/auth/SessionExpirationWarning';

// Pages
import LoginPage from './pages/LoginPage';
import CallbackPage from './pages/CallbackPage';
import DashboardPage from '../../modules/CommandCenter/FE/src/pages/DashboardPage';
import { AnalysisPage } from '@signals/analysis-ui';
import ResultsPage from '@signals/results-ui';
import ReportMapEmbedPage from '@signals/results-ui/pages/ReportMapEmbedPage';
import SettingsPage from './pages/SettingsPage';
import COPDashboard from '@signals/cop-ui';
import ItineraryPage from '../../modules/CoordinationItinerary/FE/src/pages/ItineraryPage';
import { PathfinderPage, IntelPacketPage, MissionsPage } from '@signals/pathfinder-ui';
import { DFPInsightsPage } from '@signals/pathfinder-rf-ui';
import ScheduledAnalysesPage from './pages/ScheduledAnalysesPage';
import { DashboardBuilderPage } from '@signals/dashboard-ui';
import UserContentPage from './pages/UserContentPage';

const App: React.FC = () => {
  const { loading, isAuthenticated } = useAuthStore(
    useShallow((s) => ({ loading: s.loading, isAuthenticated: s.isAuthenticated }))
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {/* Session expiration warning - only show when authenticated */}
      {isAuthenticated && <SessionExpirationWarning />}
      
      <Routes>
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<CallbackPage />} />
      <Route path="/login" element={<Navigate to="/auth/login" replace />} />
      
      {/* Pathfinder - Require analyst or administrator */}
      <Route path="/pathfinder" element={
        <ProtectedRoute requiredRole="analyst" redirectOnForbidden>
          <Layout>
            <FeatureGate path="/pathfinder">
              <PathfinderPage />
            </FeatureGate>
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/intel/mission/:missionId" element={
        <ProtectedRoute requiredRole="analyst" redirectOnForbidden>
          <Layout>
            <FeatureGate path="/intel">
              <IntelPacketPage />
            </FeatureGate>
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/intel/:packetId?" element={
        <ProtectedRoute requiredRole="analyst" redirectOnForbidden>
          <Layout>
            <FeatureGate path="/intel">
              <IntelPacketPage />
            </FeatureGate>
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/missions" element={
        <ProtectedRoute requiredRole="analyst" redirectOnForbidden>
          <Layout>
            <FeatureGate path="/intel">
              <MissionsPage />
            </FeatureGate>
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* COP Dashboard - analyst+ or PF external guests */}
      <Route path="/cop" element={
        <ProtectedRoute copAccess redirectOnForbidden>
          <Layout>
            <FeatureGate path="/cop">
              <COPDashboard />
            </FeatureGate>
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Itinerary - Available to all authenticated users */}
      <Route path="/itinerary" element={
        <Layout>
          <FeatureGate path="/itinerary">
            <ItineraryPage />
          </FeatureGate>
        </Layout>
      } />

      {/* Professional report map embed — no app chrome, iframe-friendly */}
      <Route
        path="/results/:jobId/map-embed"
        element={
          <ProtectedRoute requiredRole="viewer" redirectOnForbidden>
            <ReportMapEmbedPage />
          </ProtectedRoute>
        }
      />
      
      {/* Redirect to login if not authenticated */}
      {!isAuthenticated && <Route path="*" element={<Navigate to="/auth/login" replace />} />}
      
      {/* All other routes wrapped in Layout - Not accessible to students */}
      <Route
        path="/*"
        element={
          <ProtectedRoute requiredRole="viewer" redirectOnForbidden>
            <Layout>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={
                  <FeatureGate path="/dashboard">
                    <DashboardPage />
                  </FeatureGate>
                } />
                <Route path="/analysis" element={
                  <FeatureGate path="/analysis">
                    <AnalysisPage />
                  </FeatureGate>
                } />
                <Route path="/scheduled-analyses" element={
                  <ProtectedRoute requiredRole="analyst">
                    <FeatureGate path="/scheduled-analyses">
                      <ScheduledAnalysesPage />
                    </FeatureGate>
                  </ProtectedRoute>
                } />
                <Route path="/results/:jobId?" element={
                  <FeatureGate path="/results">
                    <ResultsPage />
                  </FeatureGate>
                } />
                <Route path="/reports" element={<Navigate to="/results" replace />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/link-analysis" element={
                  <FeatureGate path="/link-analysis">
                    <LinkAnalysisPage />
                  </FeatureGate>
                } />
                <Route path="/pattern-of-life" element={
                  <FeatureGate path="/pattern-of-life">
                    <PatternOfLifePage />
                  </FeatureGate>
                } />
                <Route path="/force-protection" element={
                  <ProtectedRoute requiredRole="analyst" redirectOnForbidden>
                    <FeatureGate path="/force-protection">
                      <DFPInsightsPage />
                    </FeatureGate>
                  </ProtectedRoute>
                } />
                <Route
                  path="/content"
                  element={
                    <FeatureGate path="/content">
                      <ProtectedRoute requiredRole="analyst" redirectOnForbidden>
                        <UserContentPage />
                      </ProtectedRoute>
                    </FeatureGate>
                  }
                />
                <Route
                  path="/dashboard-builder/:dashboardId"
                  element={
                    <ProtectedRoute requiredRole="analyst" redirectOnForbidden>
                      <DashboardBuilderPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/dashboard-builder" element={<Navigate to="/content" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
      </Routes>
    </>
  );
};

export default App;
